/// <amd-module name="Feed/Admin/Components/GlobalSettingsPanel" />
import * as React from "react";
import { connect } from "react-redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";

import { memoizeFunction } from "VSS/Core/Util/Memoize";
import { format } from "VSS/Core/Util/String";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { IdentityPicker, IIdentity } from "VSSUI/IdentityPicker";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner } from "VSSUI/Spinner";

import * as Resources from "Feed/Common/Resources";
import { FeedHubActions } from "Feed/Core/Redux/FeedHub/FeedHubActions";

import {
    getGlobalPermissionData,
    GlobalSettingsActions,
    GlobalSettingsModule,
    IGlobalSettingsAwareState
} from "../Redux/GlobalSettings";
import { IGlobalPermissionData } from "../Types";

const Actions = {
    saveChanges: GlobalSettingsActions.savePermissions,
    setError: GlobalSettingsActions.setError,
    onDismiss: FeedHubActions.hideGlobalSettings
};

interface INameListProps {
    names: string[];
}

// Unordered list of names
function NameList(props: INameListProps): JSX.Element {
    const names = props.names;
    const listItems = names.map((name, index) => <li key={index}>{name}</li>);
    return (
        <div>
            <ul>{listItems}</ul>
        </div>
    );
}

interface IErrorBarProps {
    errorMessage: string | undefined;
    onDismiss: () => void;
}

// Error bar if there is an error message, else null
function ErrorBar(props: IErrorBarProps): JSX.Element | null {
    if (!props.errorMessage) {
        return null;
    }
    return (
        <MessageBar
            className="global-settings-error-message"
            severity={MessageBarSeverity.Error}
            onDismiss={props.onDismiss}
        >
            {props.errorMessage}
        </MessageBar>
    );
}

interface IGlobalSettingsPanelProps extends IGlobalPermissionData {
    isLoading: boolean;
    isSaving: boolean;
    errorMessage: string | undefined;
}

interface IGlobalSettingsPanelState {
    isLoading: boolean;

    everyoneIsCreator: boolean;
    creatorIds: IIdentity[];
    adminIds: IIdentity[];
    customNames: string[];
}

class GlobalSettingsPanel extends VssComponent<IGlobalSettingsPanelProps & typeof Actions, IGlobalSettingsPanelState> {
    private readonly create_feed_everyone: string = "everyone-create-feed";
    private readonly create_feed_selected: string = "selected-create-feed";
    private readonly title: string = format(Resources.GlobalSettingsDialog_Title, Resources.AzureArtifacts);
    private peoplePickerProvider: PeoplePickerProvider;

    constructor(props: IGlobalSettingsPanelProps & typeof Actions, context?: IVssComponentContext) {
        super(props, context);

        this.state = {
            isLoading: true,
            everyoneIsCreator: false,
            creatorIds: new Array<IIdentity>(),
            adminIds: new Array<IIdentity>(),
            customNames: new Array<string>()
        };

        this.peoplePickerProvider = new PeoplePickerProvider(this.context.pageContext, {
            identityTypes: ["user", "group"],
            operationScopes: ["ims"],
            options: {
                MinResults: 5,
                MaxResults: 20
            }
        });
    }

    static getDerivedStateFromProps: React.GetDerivedStateFromProps<
        IGlobalSettingsPanelProps & typeof Actions,
        IGlobalSettingsPanelState
    > = (props, state) => {
        // this should be called twice, once with the initial data (props.isLoading = true),
        // once with the data loaded from the service (props.isLoading = false)
        if (state.isLoading) {
            return {
                isLoading: props.isLoading,
                everyoneIsCreator: props.everyoneIsCreator,
                creatorIds: [...props.creatorIds],
                adminIds: [...props.adminIds],
                customNames: [...props.customNames]
            };
        }
        return {};
    };

    public componentDidCatch(error, info) {
        this.props.setError(error.message);
    }

    public render(): JSX.Element {
        const isDirty = this.isDirty(
            this.props.everyoneIsCreator,
            this.state.everyoneIsCreator,
            this.props.creatorIds,
            this.state.creatorIds,
            this.props.adminIds,
            this.state.adminIds
        );
        return (
            <Panel
                titleProps={{
                    text: this.title
                }}
                onDismiss={this.props.onDismiss}
                footerButtonProps={[
                    { text: Resources.Dialog_CancelButtonText, onClick: this.props.onDismiss },
                    {
                        text: Resources.Dialog_SaveButtonText,
                        primary: true,
                        onClick: this.saveChanges,
                        disabled: !isDirty
                    }
                ]}
                overlayContent={
                    this.state.isLoading ? (
                        <Spinner label={Resources.Overlay_Loading} />
                    ) : this.props.isSaving ? (
                        <Spinner label={Resources.Overlay_Saving} />
                    ) : (
                        undefined
                    )
                }
            >
                <div>
                    <ErrorBar errorMessage={this.props.errorMessage} onDismiss={this.clearErrorMessage} />
                    {this.renderCreatorPicker()}
                    {this.renderAdministratorPicker()}
                    {this.renderCustomNamesList()}
                </div>
            </Panel>
        );
    }

    private renderCreatorPicker(): JSX.Element {
        const creatorRadioSelection = this.state.everyoneIsCreator
            ? this.create_feed_everyone
            : this.create_feed_selected;
        return (
            <div className="feed-global-settings-panel-section">
                <h2 className="title-s">{Resources.GlobalSettingsDialogCreatePermissions_Text}</h2>
                <div>
                    <RadioButtonGroup
                        className="select-feed-creators-radio-buttons"
                        selectedButtonId={creatorRadioSelection}
                        onSelect={this.onCreatorsRadioSelected}
                    >
                        <RadioButton
                            key={this.create_feed_everyone}
                            id={this.create_feed_everyone}
                            text={Resources.GlobalSettingsDialog_FeedCreatePermissionsAll}
                            disabled={this.state.isLoading}
                        />
                        <RadioButton
                            key={this.create_feed_selected}
                            id={this.create_feed_selected}
                            text={Resources.GlobalSettingsDialog_FeedCreatePermissionsAdmin}
                            disabled={this.state.isLoading}
                        />
                    </RadioButtonGroup>
                    {!this.state.everyoneIsCreator && (
                        <IdentityPicker
                            pickerProvider={this.peoplePickerProvider}
                            onIdentityAdded={this.onCreatorIdentityAdded}
                            onIdentitiesRemoved={this.onCreatorIdentitiesRemoved}
                            onIdentityRemoved={this.onCreatorIdentityRemoved}
                            selectedIdentities={this.state.creatorIds}
                        />
                    )}
                </div>
            </div>
        );
    }

    private renderAdministratorPicker(): JSX.Element {
        return (
            <div className="feed-global-settings-panel-section">
                <h2 className="title-s">{Resources.GlobalSettingsDialogAdminPermissions_Text}</h2>
                <div className="feed-global-settings-panel-description">
                    {Resources.GlobalSettingsDialog_FeedAdminExplanation}
                </div>
                <IdentityPicker
                    pickerProvider={this.peoplePickerProvider}
                    onIdentityAdded={this.onAdminIdentityAdded}
                    onIdentitiesRemoved={this.onAdminIdentitiesRemoved}
                    onIdentityRemoved={this.onAdminIdentityRemoved}
                    selectedIdentities={this.state.adminIds}
                />
            </div>
        );
    }

    // The custom names list is only shown if there are custom permissions set - this is very unlikely
    private renderCustomNamesList(): JSX.Element {
        if (this.state.customNames.length > 0) {
            return (
                <div className="feed-global-settings-panel-section">
                    <h2 className="title-s">{Resources.GlobalSettingsDialogCustomPermissions_Text}</h2>
                    <div className="feed-global-settings-panel-description">
                        {Resources.GlobalSettingsDialog_FeedCustomExplanation}
                    </div>
                    <NameList names={this.state.customNames} />
                </div>
            );
        }
        return <div />;
    }

    // calculate whether the current values differ from the initial values
    private isDirty = memoizeFunction(
        (
            initialEveryoneIsCreator: boolean,
            everyoneIsCreator: boolean,
            initialCreatorIds: IIdentity[],
            creatorIds: IIdentity[],
            initialAdminIds: IIdentity[],
            adminIds: IIdentity[]
        ) => {
            const arraysEquivalent = (list1: IIdentity[], list2: IIdentity[]): boolean => {
                if (list1.length !== list2.length) {
                    return false;
                }

                const sortedList1 = list1.map(x => x.localId).sort();
                const sortedList2 = list2.map(x => x.localId).sort();

                for (let i = 0; i < sortedList1.length; i++) {
                    if (sortedList1[i] !== sortedList2[i]) {
                        return false;
                    }
                }
                return true;
            };

            const everyoneCreatorDirty = initialEveryoneIsCreator !== everyoneIsCreator;
            const creatorIdDirty = !everyoneIsCreator && !arraysEquivalent(initialCreatorIds, creatorIds);
            const adminIdDirty = !arraysEquivalent(initialAdminIds, adminIds);

            return everyoneCreatorDirty || creatorIdDirty || adminIdDirty;
        }
    );

    private onCreatorsRadioSelected = (selectedId: string): void => {
        if (selectedId === this.create_feed_everyone) {
            this.setState({ everyoneIsCreator: true });
        } else {
            this.setState({ everyoneIsCreator: false });
        }
    };

    private onCreatorIdentityAdded = (identity: IIdentity): void => {
        this.setState({ creatorIds: [...this.state.creatorIds, identity] });
    };

    private onCreatorIdentitiesRemoved = (identities: IIdentity[]): void => {
        const filteredCreatorIds = this.state.creatorIds.filter(
            entity => identities.filter(identity => identity.entityId === entity.entityId).length === 0
        );
        this.setState({ creatorIds: filteredCreatorIds });
    };

    private onCreatorIdentityRemoved = (identity: IIdentity): void => {
        this.onCreatorIdentitiesRemoved([identity]);
    };

    private onAdminIdentityAdded = (identity: IIdentity): void => {
        this.setState({ adminIds: [...this.state.adminIds, identity] });
    };

    private onAdminIdentitiesRemoved = (identities: IIdentity[]): void => {
        const filteredAdminIds = this.state.adminIds.filter(
            entity => identities.filter(identity => identity.entityId === entity.entityId).length === 0
        );
        this.setState({ adminIds: filteredAdminIds });
    };

    private onAdminIdentityRemoved = (identity: IIdentity): void => {
        this.onAdminIdentitiesRemoved([identity]);
    };

    private clearErrorMessage = (): void => {
        this.props.setError(undefined);
    };

    private saveChanges = (): void => {
        const permissionData: IGlobalPermissionData = {
            everyoneIsCreator: this.state.everyoneIsCreator,
            creatorIds: this.state.creatorIds,
            adminIds: this.state.adminIds,
            customNames: [] // these can't be modified
        };
        this.props.saveChanges(permissionData);
    };
}

function mapStateToProps(state: IGlobalSettingsAwareState): IGlobalSettingsPanelProps {
    const settings = state.globalSettingsState;
    return {
        ...getGlobalPermissionData(state),
        isLoading: settings.isLoading,
        isSaving: settings.isSaving,
        errorMessage: settings.errorMessage
    };
}

const ConnectedGlobalSettingsPanel = connect(mapStateToProps, Actions)(GlobalSettingsPanel);

class DynamicGlobalSettingsPanel extends VssComponent {
    public render() {
        return (
            <DynamicModuleLoader modules={[GlobalSettingsModule]}>
                <ConnectedGlobalSettingsPanel />
            </DynamicModuleLoader>
        );
    }
}

VssComponent.register("ms.feed.feed-admin.global-settings-panel", DynamicGlobalSettingsPanel);
